import React, { useEffect, useRef, useState } from 'react';
import {
    FormControl,
    FormLabel,
    GridItem,
    VStack,
    Switch,
    Text,
    SimpleGrid,
    Select,
    InputGroup,
    InputRightElement,
    IconButton, Spinner
} from '@chakra-ui/react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { useDrawer } from '../../../../../Context/DrawerContext/DrawerContext';
import { fetchLabServiceProductByMaterialNumber, updateLabServiceProduct } from "../../LabServiceProductsAPI";
import { Icon } from "@iconify/react";
import { ThemedStyledInput } from "../../../../../components/Styled";
import CustomAlert from "../../../../../components/Styled/StyledAlert";
import { ButtonStack, CustomButton } from "../../../../../components/Styled/StyledButtons";
import FormActionLabel from "../../../../../components/Styled/FormActionLabel";
import ConfirmDialog from "../../../../../Context/ConfirmDialog/ConfirmDialog";
import useSingleToast from "../../../../../hooks/UseSingleToast/UseSingleToast";
import { LabServiceProductCategories } from "./LabServiceProductsCategories";

const LabServiceProductEditForm = ({ labServiceProductMaterialNumber, refetchLabServiceProductData }) => {
    const { closeDrawer } = useDrawer();
    const notifyUserRef = useRef();
    const showToast = useSingleToast();
    const [confirmDialog, setConfirmDialog] = useState({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: () => { },
    });
    const [initialValues, setInitialValues] = useState({
        name: '',
        category: '',
        unitPrice: '',
        isActive: true
    });

    notifyUserRef.current = (options) => {
        showToast(options);
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const labServiceProductData = await fetchLabServiceProductByMaterialNumber(labServiceProductMaterialNumber);
                if (labServiceProductData) {
                    setInitialValues({
                        name: labServiceProductData.name,
                        category: labServiceProductData.category,
                        unitPrice: labServiceProductData.unitPrice,
                        isActive: labServiceProductData.isActive
                    });
                }
            } catch (error) {
                notifyUserRef.current({
                    title: 'Error',
                    description: 'Failed to fetch data',
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
            }
        };
        fetchData();
    }, [labServiceProductMaterialNumber]);

    const labServiceProductSchema = Yup.object().shape({
        name: Yup.string().required('Product name is required.'),
        category: Yup.string().required('Category is required.'),
        unitPrice: Yup.number().required('Unit price is required.').typeError('Unit price must be a number.'),
        isActive: Yup.boolean().required('Active status is required.'),
    });

    const handleSubmit = async (values, actions) => {
        try {
            await updateLabServiceProduct(labServiceProductMaterialNumber, values);
            showToast({
                title: 'Product updated successfully',
                status: 'success',
                duration: 5000,
                isClosable: true,
            });
            refetchLabServiceProductData(); // Refresh the data
            closeDrawer();
        } catch (error) {
            showToast({
                title: 'Error',
                description: error.message || 'An unexpected error occurred.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            actions.setSubmitting(false);
        }
    };

    const requestConfirm = (options) => {
        setConfirmDialog({
            isOpen: true,
            title: options.title,
            message: options.message,
            onConfirm: options.onConfirm,
        });
    };
    const handleCancel = () => {
        requestConfirm({
            title: "Cancel Confirmation",
            message: "Are you sure you want to cancel? Any unsaved changes will be lost.",
            onConfirm: closeDrawer
        });
    };
    const handleConfirm = () => {
        confirmDialog.onConfirm();
        setConfirmDialog((prevState) => ({ ...prevState, isOpen: false }));
    };

    const handleCancelDialog = () => {
        setConfirmDialog((prevState) => ({ ...prevState, isOpen: false }));
    };

    return (
        <>
            <FormActionLabel formAction="edit" formName="Lab Service Product" />
            <Formik
                initialValues={initialValues}
                validationSchema={labServiceProductSchema}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {formikProps => (
                    <Form>
                        <VStack spacing={4} align="stretch" marginY={2} marginX={8}>
                            <SimpleGrid columns={1} gap={4} px={2} py={4}>
                                <FieldControl
                                    formikProps={formikProps}
                                    name="name"
                                    label="Product Name"
                                    placeholder="Enter Product Name"
                                    icon={<Icon icon="mdi:rename-outline" />}
                                />
                                <FieldControl
                                    formikProps={formikProps}
                                    name="category"
                                    label="Category"
                                    placeholder="Select Category"
                                    component="select"
                                    options={LabServiceProductCategories}
                                />
                                <FieldControl
                                    formikProps={formikProps}
                                    name="unitPrice"
                                    label="Unit Price"
                                    placeholder="Enter Unit Price"
                                    icon={<Icon icon="ion:pricetag-sharp" />}
                                />

                                <FormControl display='flex' alignItems='center'>
                                    <FormLabel htmlFor='isActive' mb='0'>
                                        Set Active?
                                    </FormLabel>
                                    <Switch
                                        id='isActive'
                                        name="isActive"
                                        isChecked={formikProps.values.isActive}
                                        onChange={(e) => {
                                            formikProps.setFieldValue('isActive', e.target.checked);
                                            
                                        }}
                                    />
                                </FormControl>

                            </SimpleGrid>
                            <ButtonStack direction="row" style={{ justifyContent: 'flex-start' }}>
                                <CustomButton onClick={handleCancel} type="cancel" showIcon={false}>
                                    Cancel
                                </CustomButton>
                                <CustomButton
                                    type="submit"
                                    disabled={formikProps.isSubmitting}
                                    style={{
                                        opacity: formikProps.isSubmitting ? 0.7 : 1,
                                        pointerEvents: formikProps.isSubmitting ? 'none' : 'auto',
                                    }}
                                >
                                    {formikProps.isSubmitting ? (
                                        <>
                                            <Spinner size="xs" mr={2} />
                                            Updating...
                                        </>
                                    ) : 'Update'}
                                </CustomButton>
                            </ButtonStack>
                        </VStack>
                    </Form>
                )}
            </Formik>
            <ConfirmDialog
                isOpen={confirmDialog.isOpen}
                onClose={handleCancelDialog}
                onConfirm={handleConfirm}
                title={confirmDialog.title}
                message={confirmDialog.message}
            />
        </>
    );
};

const FieldControl = ({ formikProps, name, label, placeholder, component = "input", options = [], icon }) => (
    <GridItem>
        <FormControl isInvalid={formikProps.errors[name] && formikProps.touched[name]}>
            <FormLabel htmlFor={name}>{label}</FormLabel>
            <InputGroup>
                {component === "select" ? (
                    <Field as={Select} id={name} name={name} placeholder={placeholder}>
                        {options.map(option => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </Field>
                ) : (
                    <ThemedStyledInput
                        {...formikProps.getFieldProps(name)}
                        id={name}
                        placeholder={placeholder}
                    />
                )}
                {icon && (
                    <InputRightElement>
                        <IconButton
                            aria-label="Icon button label"
                            bg="blue.300"
                            _hover={{ bg: '#69C5EC' }}
                            _active={{ bg: '#4699bc' }}
                            color="white"

                        >
                            {icon}
                        </IconButton>
                    </InputRightElement>

                )}
            </InputGroup>
            {formikProps.errors[name] && formikProps.touched[name] && (
                <Text color="red.500" fontSize="sm">{formikProps.errors[name]}</Text>
            )}
        </FormControl>
    </GridItem>
);



export default LabServiceProductEditForm;

